import { useCallback, useEffect, useRef, useState } from 'react'

const allowedChannels = ['signal_indicator']

const connectWebsocket = async (setWs: React.Dispatch<React.SetStateAction<WebSocket | undefined>>) => {
  setWs(new WebSocket(`${globalThis.isJest ? `wss://${window.location.hostname}` : ''}/api/stream/all`))
}

const subscribeWebsocket = (nodeId: number, ws: WebSocket, subscribeChannels: string[]) => {
  subscribeChannels.forEach((channel) => {
    // Subscribe (or resubscribe) the node to the websocket
    const dataPayload = {
      action: `subscribe:data`,
      arguments: {
        nodeId,
        channelName: channel,
      },
    }
    if (ws.readyState === ws.OPEN) ws.send(JSON.stringify(dataPayload))
  })
}

const unsubscribeWebsocket = (nodeId: number, ws: WebSocket) => {
  // Unsubscribe the node to the websocket
  const payload = {
    action: `unsubscribe:data`,
    arguments: {
      nodeId,
    },
  }
  if (ws.readyState === ws.OPEN) ws.send(JSON.stringify(payload))
}

/**
 * @description This hook manages the websocket connection, subscriptions, and message handling.
 * It will automatically reconnect if the connection is lost and manage subscriptions based on the provided node IDs.
 *
 * @param subscribeIds - List of node IDs to subscribe to
 * @param onMessage - Callback function to handle incoming websocket messages
 * @param shouldBeAlive - Whether the websocket should be alive or not (default=true)
 * @param subscribeChannels - List of channels to subscribe to (default list is used if not provided)
 *
 * @example
 * useWebsocket({
 *   subscribeIds: [1],
 *   onMessage: (message) => console.log(message),
 *   subscribeChannels: ['temp', 'hum'],
 * })
 */
const useWebsocket = ({
  subscribeIds,
  onMessage,
  shouldBeAlive = true,
  subscribeChannels = allowedChannels,
}: {
  subscribeIds: number[]
  onMessage: (props: WebsocketDataType['payload']) => void
  shouldBeAlive?: boolean
  subscribeChannels?: string[]
}) => {
  const [ws, setWs] = useState<WebSocket | undefined>()
  const subscribeIdsRef = useRef<number[]>(subscribeIds)

  const keepAliveIntervalRef = useRef<number | undefined>(undefined)

  useEffect(() => {
    if (ws) {
      if (!shouldBeAlive) {
        ws.close(1000)
        clearInterval(keepAliveIntervalRef.current as unknown as number)
        setWs(undefined)
      }
    } else if (shouldBeAlive) connectWebsocket(setWs)
  }, [ws, shouldBeAlive])

  useEffect(() => {
    if (ws?.readyState === ws?.OPEN) {
      clearInterval(keepAliveIntervalRef.current as unknown as number)
      keepAliveIntervalRef.current = setInterval(() => {
        if (!ws) return
        if (ws.readyState === ws.OPEN) {
          const payload = { action: `keepalive` }
          ws.send(JSON.stringify(payload))
        }
        // websocket timeout is about a minute. This keep alive timer is 50 seconds to account for any delays or something.
      }, 1000 * 50)
    }

    return () => clearInterval(keepAliveIntervalRef.current as unknown as number)
  }, [ws, ws?.readyState, ws?.OPEN])

  const manageSubscriptions = useCallback(() => {
    if (ws && ws.readyState === ws.OPEN) {
      // Unsubscribe nodes that are no longer in list
      subscribeIdsRef.current.forEach((id) => {
        if (!subscribeIds.includes(id)) unsubscribeWebsocket(id, ws)
      })
      // Subscribe nodes that are in list now but weren't previously
      subscribeIds.forEach((id) => {
        if (!subscribeIdsRef.current?.includes(id)) subscribeWebsocket(id, ws, subscribeChannels)
      })
      subscribeIdsRef.current = subscribeIds
    }
  }, [subscribeIds, ws, subscribeChannels])

  const reconnectWebsocket = useCallback(() => connectWebsocket(setWs), [])

  useEffect(() => {
    if (JSON.stringify(subscribeIdsRef.current) !== JSON.stringify(subscribeIds)) {
      manageSubscriptions()
    }
  }, [subscribeIds, manageSubscriptions])

  useEffect(() => {
    if (ws) {
      ws.onopen = () => {
        subscribeIds?.forEach((id) => subscribeWebsocket(id, ws, subscribeChannels))
      }
    }
  }, [subscribeIds, ws, subscribeChannels])

  useEffect(() => {
    if (ws) {
      ws.onmessage = (message) => {
        const websocketMessage = JSON.parse(message.data) as WebsocketDataType
        if (websocketMessage.statusCode === 403 || websocketMessage.statusCode === 401) {
          //   handleLogout()
          return
        }
        if (
          websocketMessage.type !== 'data' ||
          websocketMessage.statusCode !== 200 ||
          (websocketMessage.type === 'data' && !subscribeChannels.includes(websocketMessage.payload.channelName))
        )
          return

        onMessage(websocketMessage.payload)
      }
      ws.onerror = () => {}
      ws.onclose = (response: { code?: number; message?: string; wasClean?: boolean }) => {
        // If the websocket was closed unintentionally, re-open the connection
        if (response.code && response.code !== 1000 && response.code !== 1006) {
          reconnectWebsocket()
        }
      }
    }
  }, [ws, subscribeChannels, reconnectWebsocket, onMessage])
}

export default useWebsocket
